import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { strings } from 'strings';
import { useFormContext } from 'react-hook-form';
import { theme } from 'theme';
import { NavLink } from 'react-router-dom';

import { Text } from '../../../../../../../../shared/Typhography';
import { Button, Input, Splash } from '../../../../../../../../shared/Components';
import { Icon } from '../Icon/Icon';
import { ItemContainer } from '../ItemContainer/ItemContainer';
import useArtist from '../PuzzleItem/hooks/useArtist';

import useAddPuzzle from './hooks/useAddPuzzle';

const PlusIcon = styled.div`
  width: 84px;
  height: 84px;
  border-radius: 15px;
  font-size: 80px;
  color: ${theme.colors.white};
  background-color: ${theme.colors.purple};
  display: flex;
  justify-content: center;
  line-height: 72px;
  font-weight: bold;
  margin-bottom: 10px;
`;

interface AddNewItemProps {
  onAddItem?: (puzzle: { puzzleId: string; name: string; icon: string }) => void;
  disabled?: boolean;
}

const AddNewItem = ({ disabled, onAddItem }: AddNewItemProps) => {
  const { getValues } = useFormContext();

  const [isAdding, setIsAdding] = useState(false);

  const data = getValues();

  const puzzleIdsInEvent = useMemo(() => {
    const puzzleIds: string[] = [];

    data.levels.forEach((level: any) => {
      const ids = level.puzzles.map((puzzle: any) => puzzle.puzzleId);

      puzzleIds.push(...ids);
    });

    return puzzleIds;
  }, [data.levels]);

  const { submit, reset, error, isLoading, control, puzzleData, puzzleId } = useAddPuzzle({
    onAddItem,
    puzzleIdsInEvent,
  });
  const { state: artistState, data: artistData } = useArtist({ artistId: puzzleData?.Publisher });

  const toggleIsAdding = useCallback(() => {
    setIsAdding(!isAdding);
    reset();
  }, [isAdding]);

  if (artistState === 'loading' && isLoading) {
    return (
      <ItemContainer>
        <Splash />
      </ItemContainer>
    );
  }

  return (
    <ItemContainer>
      {!puzzleData && <PlusIcon>+</PlusIcon>}
      {puzzleData && (
        <NavLink to={`/view-puzzle/${puzzleId}`} target="_blank">
          <Icon src={puzzleData.iconURL} />
        </NavLink>
      )}

      {isAdding && (
        <Input
          required
          placeholder={strings.gameEvent.form.placeholder.puzzleId}
          name="puzzleId"
          control={control}
          type="text"
          width="100%"
        />
      )}
      {puzzleId && !isLoading && !puzzleData && (
        <Text style={{ textAlign: 'center', margin: 10 }}>{error}</Text>
      )}

      {!puzzleData && (!puzzleId || isLoading) && <div style={{ height: '38px' }} />}

      {!!puzzleData && (
        <NavLink to={`/view-puzzle/${puzzleId}`} target="_blank">
          <Text>{puzzleData.name}</Text>
        </NavLink>
      )}
      {!!artistData && (
        <NavLink to={`/view-artist/${artistData.uid}`} target="_blank">
          <Text>{artistData.username}</Text>
        </NavLink>
      )}

      {puzzleData && (
        <Button
          style={{ minWidth: 150 }}
          variant="primary"
          disabled={disabled}
          onClick={submit}
          height={30}
        >
          {strings.gameEvent.form.buttons.confirm}
        </Button>
      )}

      {!puzzleData && (
        <Button
          style={{ minWidth: 150 }}
          variant={isAdding ? 'danger' : 'primary'}
          disabled={disabled}
          onClick={toggleIsAdding}
          height={30}
        >
          {isAdding
            ? strings.gameEvent.form.buttons.cancel
            : strings.gameEvent.form.buttons.addPuzzle}
        </Button>
      )}
    </ItemContainer>
  );
};

export default AddNewItem;
